import { Button, IconButton } from '@fukuri-admins/ui/common';
import { FieldWrapper } from '@fukuri-admins/ui/common/Form/FieldWrapper';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@fukuri-admins/ui/common/Form/FormItems';
import { CrossIcon, PlusIcon } from '@fukuri-admins/ui/common/Icons';
import type { MonthOption, MonthRowWithAvailable } from '../types';
import styles from './index.module.scss';

type Props = {
  /** 月選択行のリスト */
  monthRows: MonthRowWithAvailable[];
  /** 月選択変更ハンドラー */
  onChangeMonth: (id: string, month: MonthOption | null) => void;
  /** 実施月追加ハンドラー */
  onAddMonth: () => void;
  /** 実施月削除ハンドラー */
  onRemoveMonth: (id: string) => void;
  /** 保存ハンドラー */
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  /** キャンセルハンドラー */
  onCancel: () => void;
  /** 12ヶ月すべて選択済みの場合 true */
  isAddDisabled: boolean;
  /** 保存ボタンのローディング状態 */
  isSaveLoading?: boolean;
};

export const StressAssessmentSchedulePresentation: React.FC<Props> = ({
  monthRows,
  onChangeMonth,
  onAddMonth,
  onRemoveMonth,
  onSubmit,
  onCancel,
  isAddDisabled,
  isSaveLoading,
}) => {
  return (
    <form className={styles.container} onSubmit={onSubmit}>
      <div className={styles.contentWrapper}>
        <h1 className={styles.title}>スケジュールの設定</h1>

        <p className={styles.description}>
          ストレスチェックを実施する月を設定すると、実施月の前月と当月の1日に管理者宛にリマインド通知を送信します。
        </p>

        <div className={styles.fieldSection}>
          <FieldWrapper
            htmlFor='schedule-month'
            labelType='required'
            labelText='実施月'
            beforeMessage='1年以内ごとに1回の実施が義務付けられています。'
          >
            <div className={styles.monthRowList}>
              {monthRows.map(row => {
                return (
                  <div key={row.id} className={styles.monthRow}>
                    <Select
                      value={row.month !== null ? String(row.month) : ''}
                      onValueChange={value => {
                        const parsed = Number.parseInt(value, 10);
                        onChangeMonth(
                          row.id,
                          Number.isNaN(parsed) ? null : (parsed as MonthOption)
                        );
                      }}
                    >
                      <SelectTrigger className={styles.monthSelect}>
                        <SelectValue placeholder='月を選択' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {row.availableMonths.map(month => (
                            <SelectItem key={month} value={String(month)}>
                              {month}月
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>

                    <IconButton
                      icon={<CrossIcon size='sm' />}
                      aria-label={`${row.month != null ? `${row.month}月の` : ''}実施月を削除`}
                      variant='ghost'
                      size='sm'
                      onClick={() => onRemoveMonth(row.id)}
                    />
                  </div>
                );
              })}
            </div>
          </FieldWrapper>

          <div className={styles.addButton}>
            <Button
              variant='secondary'
              size='sm'
              icon={<PlusIcon size='sm' />}
              iconPosition='left'
              disabled={isAddDisabled}
              onClick={onAddMonth}
            >
              実施月を追加
            </Button>
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant='outline' type='button' onClick={onCancel}>
            キャンセル
          </Button>
          <Button type='submit' disabled={isSaveLoading}>
            保存
          </Button>
        </div>
      </div>
    </form>
  );
};
